import { Heart, ChatCircle, PaperPlaneTilt, BookmarkSimple, ThumbsUp, ShareFat, Globe, DotsThreeVertical, MusicNoteSimple } from "@phosphor-icons/react";
import { renderSocial } from "@/lib/richText";

// Authentic per-platform post preview — native system font + real platform chrome, so staff see
// exactly how a post will look once it's live, not a generic image-then-paragraph block.
const NATIVE_FONT = { fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif" };
const HASHTAG_COLOR = { facebook: "#0866FF", instagram: "#00376B", linkedin: "#0A66C2", tiktok: "#ffffff" };

function Avatar({ logoUrl, name, size = 36 }) {
  const initial = (name || "B").trim().charAt(0).toUpperCase();
  return logoUrl ? (
    <img src={logoUrl} alt="" style={{ width: size, height: size }} className="rounded-full object-cover shrink-0 border border-black/5" data-testid="mockup-avatar" />
  ) : (
    <div style={{ width: size, height: size }} className="rounded-full bg-[#5FA8B4] text-white flex items-center justify-center font-bold shrink-0" data-testid="mockup-avatar-fallback">
      {initial}
    </div>
  );
}

function Media({ url, kind, className, bare = false }) {
  if (!url) return null;
  if (kind === "video") {
    return bare
      ? <video src={url} muted playsInline loop autoPlay preload="metadata" className={className} data-testid="mockup-media-video"
          onError={(e) => { e.currentTarget.style.display = "none"; }} />
      : <video src={url} controls className={className} data-testid="mockup-media-video" />;
  }
  return <img src={url} alt="" loading="lazy" className={className} data-testid="mockup-media-image" />;
}

export function PlatformMockup({ platform, mediaUrl, mediaKind, caption, brandName, brandLogoUrl }) {
  const name = brandName || "Your Brand";
  const hashtagColor = HASHTAG_COLOR[platform] || "#0866FF";

  if (platform === "instagram") {
    return (
      <div style={NATIVE_FONT} className="w-full max-w-[420px] bg-white rounded-[2px] ring-1 ring-black/10 overflow-hidden" data-testid="mockup-instagram">
        <div className="flex items-center justify-between px-3 py-2.5">
          <div className="flex items-center gap-2.5">
            <Avatar logoUrl={brandLogoUrl} name={name} size={32} />
            <span className="text-[13px] font-semibold text-[#262626]">{name.toLowerCase().replace(/\s+/g, "")}</span>
          </div>
          <DotsThreeVertical size={18} className="text-[#262626]" />
        </div>
        {/* Real Instagram preserves the source aspect ratio (roughly 1:1 to 4:5) rather than
            force-cropping every image into a fixed box — a forced aspect-[4/5]+object-cover here
            would silently crop square/landscape images and misrepresent what will actually post. */}
        {mediaUrl && (
          <div className="bg-black flex items-center justify-center max-h-[520px]" data-testid="mockup-instagram-media-frame">
            <Media url={mediaUrl} kind={mediaKind} className="w-full h-auto max-h-[520px] object-contain" />
          </div>
        )}
        <div className="flex items-center justify-between px-3 pt-2.5 pb-1">
          <div className="flex items-center gap-4">
            <Heart size={24} className="text-[#262626]" />
            <ChatCircle size={24} className="text-[#262626]" />
            <PaperPlaneTilt size={22} className="text-[#262626]" />
          </div>
          <BookmarkSimple size={22} className="text-[#262626]" />
        </div>
        <div className="px-3 pb-3 text-[13px] leading-snug text-[#262626]">
          <span className="font-semibold mr-1">{name.toLowerCase().replace(/\s+/g, "")}</span>
          <span className="whitespace-pre-line">{renderSocial(caption, hashtagColor)}</span>
        </div>
      </div>
    );
  }

  if (platform === "facebook" || platform === "linkedin") {
    const isLinkedin = platform === "linkedin";
    const titleCased = name.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
    return (
      <div style={NATIVE_FONT} className="w-full max-w-[420px] bg-white rounded-lg ring-1 ring-black/10 overflow-hidden" data-testid={`mockup-${platform}`}>
        <div className="flex items-center justify-between px-3 pt-3 pb-2">
          <div className="flex items-center gap-2.5">
            <Avatar logoUrl={brandLogoUrl} name={name} size={40} />
            <div>
              <p className="text-[14px] font-semibold text-[#050505] leading-tight">{titleCased}</p>
              {isLinkedin && <p className="text-[11px] text-[#00000099] leading-tight">Health & wellness</p>}
              <p className="text-[11px] text-[#00000099] flex items-center gap-1">Just now <Globe size={11} /></p>
            </div>
          </div>
          <DotsThreeVertical size={18} className="text-[#00000099]" />
        </div>
        <div className="px-3 pb-2 text-[14px] leading-snug text-[#050505] whitespace-pre-line">
          {renderSocial(caption, hashtagColor)}
        </div>
        {/* object-contain, not object-cover — never crop a Facebook/LinkedIn image to fit the box;
            letterbox on the neutral background instead so the real aspect ratio is always visible. */}
        {mediaUrl && (
          <div className="bg-[#f0f2f5] flex items-center justify-center max-h-[420px]" data-testid={`mockup-${platform}-media-frame`}>
            <Media url={mediaUrl} kind={mediaKind} className="w-full h-auto max-h-[420px] object-contain" />
          </div>
        )}
        <div className="flex items-center gap-5 px-3 py-2 text-[#00000099] text-[13px] font-medium border-t border-black/5 mt-1">
          <span className="flex items-center gap-1.5"><ThumbsUp size={16} /> Like</span>
          <span className="flex items-center gap-1.5"><ChatCircle size={16} /> Comment</span>
          <span className="flex items-center gap-1.5"><ShareFat size={16} /> {isLinkedin ? "Repost" : "Share"}</span>
        </div>
      </div>
    );
  }

  if (platform === "tiktok") {
    return (
      <div style={NATIVE_FONT} className="w-full max-w-[320px] aspect-[9/16] bg-black rounded-[14px] ring-1 ring-black/20 overflow-hidden relative" data-testid="mockup-tiktok">
        <Media url={mediaUrl} kind={mediaKind || "video"} bare className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
        <div className="absolute right-2.5 bottom-16 flex flex-col items-center gap-4 text-white">
          <Avatar logoUrl={brandLogoUrl} name={name} size={36} />
          <div className="flex flex-col items-center gap-0.5"><Heart size={26} weight="fill" /></div>
          <div className="flex flex-col items-center gap-0.5"><ChatCircle size={24} weight="fill" /></div>
          <div className="flex flex-col items-center gap-0.5"><ShareFat size={24} weight="fill" /></div>
          <MusicNoteSimple size={22} className="animate-spin" style={{ animationDuration: "3s" }} />
        </div>
        <div className="absolute left-3 right-14 bottom-3 text-white text-[13px] leading-snug">
          <p className="font-semibold mb-1">{name.toLowerCase().replace(/\s+/g, "")}</p>
          <p className="line-clamp-3 whitespace-pre-line">{renderSocial(caption, hashtagColor)}</p>
        </div>
      </div>
    );
  }

  return null;
}
